import styles from "../../styles";

/**
 * Component for rendering popups
 */
export class PopupComponent extends HTMLElement {
    private _content: string = "";
    private _onClose: (() => void) | null = null;

    /**
     * Set the popup content
     */
    public set content(content: string) {
        this._content = content;
        this.render();
    }

    /**
     * Set the close handler
     */
    public set onClose(handler: () => void) {
        this._onClose = handler;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    /**
     * Render the component
     */
    private render(): void {
        const content: string = `
            <style>${styles.popups}</style>
            <div class="popup-overlay">
                <div class="popup">
                    <div class="popup-close">&times;</div>
                    <div class="popup-content">
                        ${this._content}
                    </div>
                </div>
            </div>
        `;
        this.innerHTML = content;
    }

    /**
     * Setup event listeners
     */
    private setupEventListeners(): void {
        // Close button click
        const closeButton: HTMLElement | null = this.querySelector(".popup-close");
        if (closeButton) {
            closeButton.addEventListener("click", this.handleClose.bind(this));
        }

        // Outside click
        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        if (overlay) {
            overlay.addEventListener("click", this.handleOutsideClick.bind(this));
        }

        // Prevent clicks inside popup from closing it
        const popup: HTMLElement | null = this.querySelector(".popup");
        if (popup) {
            popup.addEventListener("click", (e: Event) => e.stopPropagation());
        }
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        const closeButton: HTMLElement | null = this.querySelector(".popup-close");
        if (closeButton) {
            closeButton.removeEventListener("click", this.handleClose.bind(this));
        }

        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        if (overlay) {
            overlay.removeEventListener("click", this.handleOutsideClick.bind(this));
        }

        const popup: HTMLElement | null = this.querySelector(".popup");
        if (popup) {
            popup.removeEventListener("click", (e: Event) => e.stopPropagation());
        }
    }

    /**
     * Handle close button click
     */
    private handleClose(): void {
        if (this._onClose) {
            this._onClose();
        }
        this.remove();
    }

    /**
     * Handle click outside the popup
     */
    private handleOutsideClick(event: MouseEvent): void {
        if (event.target === this.querySelector(".popup-overlay")) {
            this.handleClose();
        }
    }
}
